import { BOOKING_URL, trackBooking } from "@/lib/booking";
import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ChevronLeft, Clock, ArrowRight } from "lucide-react";
import { BLOG_POSTS, BLOG_AUTHOR, getPost, formatDate } from "@/data/blog";
import { useSeo } from "@/lib/seo";
import { SiteHeader, SiteFooter } from "@/components/SiteHeader";
import { WhatsAppShare } from "@/components/ShareButtons";

const ORIGIN = "https://purohithconnect.com";
const absImg = (img) => (img.startsWith("http") ? img : `${ORIGIN}${img}`);

const Section = ({ s, i }) => (
  <section className="mt-9" data-testid={`blog-section-${i}`}>
    {s.kn && <p className="font-baloo text-base text-[#B23A2B]">{s.kn}</p>}
    <h2 className="mt-0.5 font-eczar text-2xl font-semibold text-[#3B221B]">{s.h2}</h2>
    {(s.p || []).map((t, j) => <p key={j} className="mt-3 font-mukta text-[16px] leading-relaxed text-[#3B221B]">{t}</p>)}
    {s.list && (
      <ul className="mt-3 space-y-2">
        {s.list.map((t, j) => (
          <li key={j} className="flex gap-2.5 font-mukta text-[15px] leading-relaxed text-[#3B221B]">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#A8722A]" aria-hidden="true" />{t}
          </li>
        ))}
      </ul>
    )}
    {s.note && (
      <div className="mt-4 rounded-[14px] border border-[#3E6B4F]/30 bg-[#3E6B4F]/[0.06] p-4 font-mukta text-sm text-[#3B221B]">
        <span className="font-semibold">ಸೂಚನೆ · Note:</span> {s.note}
      </div>
    )}
  </section>
);

export default function BlogPost() {
  const { slug } = useParams();
  const p = getPost(slug);
  const idx = BLOG_POSTS.findIndex((x) => x.slug === slug);
  const related = p ? BLOG_POSTS.filter((x) => x.slug !== p.slug && x.cat === p.cat).slice(0, 3) : [];
  const more = p && related.length < 3 ? BLOG_POSTS.filter((x) => x.slug !== p.slug && !related.includes(x)).slice(0, 3 - related.length) : [];
  const next = idx >= 0 ? BLOG_POSTS[(idx + 1) % BLOG_POSTS.length] : null;
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  useSeo({
    title: p ? `${p.titleEn} — ${p.titleKn} | PurohithConnect Blog` : "Blog | PurohithConnect",
    description: p ? p.excerpt : "Pooja, muhurta and purohith guides for Bengaluru.",
    canonicalPath: p ? `/blog/${p.slug}` : "/blog",
    jsonLd: p ? [
      { "@context": "https://schema.org", "@type": "BlogPosting", headline: p.titleEn, alternativeHeadline: p.titleKn, description: p.excerpt, image: absImg(p.img), datePublished: p.date, dateModified: p.date, inLanguage: ["en", "kn"], keywords: p.keywords.join(", "), articleSection: p.cat,
        author: { "@type": "Organization", name: BLOG_AUTHOR }, publisher: { "@type": "Organization", name: "PurohithConnect", url: ORIGIN }, mainEntityOfPage: `${ORIGIN}/blog/${p.slug}`, speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "article h2", "article p"] } },
      ...(p.howto ? [{ "@context": "https://schema.org", "@type": "HowTo", name: `${p.titleEn} — step by step`, description: p.excerpt, step: p.howto.map((s, i) => ({ "@type": "HowToStep", position: i + 1, name: s, text: s })) }] : []),
      { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: p.faqs.map((x) => ({ "@type": "Question", name: x.q, acceptedAnswer: { "@type": "Answer", text: x.a } })) },
      { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Blog", item: `${ORIGIN}/blog` },
        { "@type": "ListItem", position: 2, name: p.cat, item: `${ORIGIN}/blog` },
        { "@type": "ListItem", position: 3, name: p.titleEn, item: `${ORIGIN}/blog/${p.slug}` }] },
    ] : undefined,
  });

  if (!p) return <Navigate to="/blog" replace />;

  return (
    <div className="min-h-screen bg-[#FBF6EC] text-[#3B221B]">
      <SiteHeader />
      <article className="mx-auto max-w-3xl px-4 py-10 md:px-8">
        <nav className="flex flex-wrap items-center gap-1 font-mukta text-sm text-[#6B5044]" aria-label="Breadcrumb" data-testid="blog-breadcrumb">
          <Link to="/blog" className="inline-flex items-center gap-1 hover:text-[#3B221B]" data-testid="blog-back"><ChevronLeft size={16} /> Blog</Link>
          <span>·</span><span>{p.cat}</span>
        </nav>
        <p className="mt-5 font-baloo text-xl text-[#B23A2B]" data-testid="blog-post-title-kn">{p.titleKn}</p>
        <h1 className="mt-1 font-eczar text-3xl font-bold leading-tight sm:text-4xl" data-testid="blog-post-title">{p.titleEn}</h1>
        <p className="mt-3 font-mukta text-base text-[#6B5044]">{p.excerpt}</p>
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 font-mukta text-xs text-[#6B5044]" data-testid="blog-post-meta">
          <span>{BLOG_AUTHOR}</span><span>{formatDate(p.date)}</span>
          <span className="inline-flex items-center gap-1"><Clock size={12} /> {p.readMins} min read</span>
        </div>
        <div className="mt-6 overflow-hidden rounded-[18px] border border-[#A8722A]/20 bg-[#F3EADA]">
          <img src={p.img} alt={p.titleEn} className="h-64 w-full object-cover sm:h-80" data-testid="blog-post-hero-img" />
        </div>

        {p.sections.map((s, i) => <Section key={i} s={s} i={i} />)}

        <section className="mt-10" data-testid="blog-faq-section">
          <p className="font-baloo text-base text-[#B23A2B]">ಪ್ರಶ್ನೆಗಳು</p>
          <h2 className="mt-0.5 font-eczar text-2xl font-semibold">Frequently asked questions</h2>
          <div className="mt-3 space-y-2">
            {p.faqs.map((f, i) => (
              <details key={i} open={i === 0} className="group rounded-[14px] border border-[#A8722A]/20 bg-[#FFFDF8] p-4" data-testid={`blog-faq-${i}`}>
                <summary className="cursor-pointer list-none font-eczar text-lg font-semibold text-[#3B221B] marker:content-none">{f.q}</summary>
                <p className="mt-2 font-mukta text-[15px] text-[#6B5044]">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mt-8 rounded-[16px] border border-[#A8722A]/25 bg-[#F3EADA]/70 p-5" data-testid="blog-links">
          <p className="font-baloo text-base text-[#B23A2B]">ಮುಂದೆ ಓದಿ</p>
          <h2 className="font-eczar text-xl font-semibold">Useful links</h2>
          <ul className="mt-2 space-y-1.5">
            {p.links.map((l, i) => (
              <li key={i}><Link to={l.to} className="inline-flex items-center gap-1.5 font-mukta text-sm font-semibold text-[#B23A2B] hover:gap-2.5 transition-all" data-testid={`blog-link-${i}`}>{l.label} <ArrowRight size={14} /></Link></li>
            ))}
          </ul>
        </section>

        <div className="mt-6 overflow-hidden rounded-[16px] bg-[#3B221B] p-6 text-center" data-testid="blog-book-cta">
          <p className="font-eczar text-lg font-semibold text-[#FBF6EC]">Ready to perform this at home?</p>
          <p className="mt-1 font-mukta text-sm text-[#FBF6EC]/70">Book a KYC-verified purohith in Bengaluru, matched to your tradition and language.</p>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackBooking({ location: "blog-post" })} className="mt-4 inline-block rounded-full bg-[#B23A2B] px-6 py-2.5 font-mukta font-semibold text-[#FFFDF8] hover:bg-[#9a2f22]" data-testid="blog-post-book-cta">
            Book a Purohith ↗
          </a>
        </div>


        <div className="mt-6 flex flex-wrap items-center gap-3">
          <WhatsAppShare text={`${p.titleEn} — ${ORIGIN}/blog/${p.slug}`} testId="blog-whatsapp-share" />
          <p className="font-mukta text-xs text-[#6B5044]">Share this guide with family planning a pooja.</p>
        </div>

        <section className="mt-12" data-testid="blog-related">
          <h2 className="font-eczar text-2xl font-semibold">More to read</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {[...related, ...more].map((r) => (
              <Link key={r.slug} to={`/blog/${r.slug}`} className="group rounded-[14px] border border-[#A8722A]/20 bg-[#FFFDF8] p-4 transition-shadow hover:shadow-md" data-testid={`blog-related-${r.slug}`}>
                <span className="font-mukta text-[11px] font-semibold text-[#A8722A]">{r.cat}</span>
                <p className="mt-1 font-baloo text-sm text-[#B23A2B]">{r.titleKn}</p>
                <h3 className="font-eczar text-base font-semibold leading-snug">{r.titleEn}</h3>
              </Link>
            ))}
          </div>
        </section>

        {next && (
          <Link to={`/blog/${next.slug}`} className="mt-8 flex items-center justify-between rounded-[14px] border border-[#A8722A]/25 bg-[#FFFDF8] p-4 font-mukta text-sm hover:border-[#A8722A]" data-testid="blog-next">
            <span className="text-[#6B5044]">Next article</span>
            <span className="inline-flex items-center gap-1 font-semibold text-[#3B221B]">{next.titleEn} <ArrowRight size={15} /></span>
          </Link>
        )}
      </article>
      <SiteFooter />
    </div>
  );
}
